// components/extraction/IdentityExtractionCard.tsx
import { FC, useCallback, useMemo } from 'react';
import { Card, Tabs, Typography, Flex, Button } from 'antd';
import type { TabsProps } from 'antd';
import Title from 'antd/es/typography/Title';
import { CheckCircleTwoTone, CloseOutlined } from '@ant-design/icons';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { ConfidenceIndicator } from '@/components/Elements';
import { normalizeExtractedValue } from './utils/normalize-extracted-value.util';
import { FileExtractionFieldKey } from './types/file-extraction-field-key.types';
const { Text } = Typography;

type IdentityFieldNode = any; // backend shape is flexible

interface IdentityDocument {
  fields?: Record<string, IdentityFieldNode>;
}

interface IdentityExtractionCardProps {
  selectedFileName?: string | null;
  documents: IdentityDocument[]; // already normalized to an array
  rawText?: string;
  selectedFieldKeys: Set<string>;
  setSelectedFieldKeys: React.Dispatch<React.SetStateAction<Set<string>>>;
  getIdFieldValue?: (entry: {
    key: FileExtractionFieldKey;
    value: string;
  }) => void;
  closable?: boolean;
  onClose?: () => void;
}

const IdentityExtractionCard: FC<IdentityExtractionCardProps> = ({
  selectedFileName,
  documents,
  rawText,
  selectedFieldKeys,
  setSelectedFieldKeys,
  getIdFieldValue,
  closable,
  onClose,
}) => {
  const toggleKey = useCallback(
    (key: string, content: string) => {
      setSelectedFieldKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
          const normalized = normalizeExtractedValue(
            key as FileExtractionFieldKey,
            content,
          );
          getIdFieldValue?.({
            key: key as FileExtractionFieldKey,
            value: normalized,
          });
        }
        return next;
      });
    },
    [setSelectedFieldKeys, getIdFieldValue],
  );

  const renderField = useCallback(
    (key: string, value: IdentityFieldNode): React.ReactNode => {
      const isSelected = selectedFieldKeys.has(key);

      // leaf (string) node
      if (
        typeof value?.content === 'string' ||
        typeof value?.valueString === 'string'
      ) {
        const content = String(value?.content ?? value?.valueString ?? '');
        return (
          <div
            key={key}
            style={{ margin: '4px 0', cursor: 'pointer' }}
            onClick={() => toggleKey(key, content)}
          >
            <Flex gap={10} align="center">
              <Text strong>{key}</Text>: <Text>{content}</Text>
              {isSelected && <CheckCircleTwoTone twoToneColor="#52c41a" />}
            </Flex>
            <ConfidenceIndicator confidence={value?.confidence} />
          </div>
        );
      }

      // array of objects
      if (value?.type === 'array' && Array.isArray(value.valueArray)) {
        return (
          <div key={key} style={{ margin: '8px 0' }}>
            <Text strong>{key}</Text>
            {value.valueArray.map((item: any, i: number) =>
              item?.type === 'object' && item?.valueObject ? (
                <Card key={`${key}-${i}`} size="small" style={{ marginTop: 6 }}>
                  {Object.entries(item.valueObject).map(([subKey, subVal]) => (
                    <div key={`${key}.${String(subKey)}`}>
                      {renderField(`${key}.${String(subKey)}`, subVal)}
                    </div>
                  ))}
                </Card>
              ) : null,
            )}
          </div>
        );
      }

      // nested object
      if (value?.type === 'object' && value.valueObject) {
        return (
          <div key={key} style={{ margin: '8px 0' }}>
            <Text strong>{key}</Text>
            <Card size="small" style={{ marginTop: 6 }}>
              {Object.entries(value.valueObject).map(([subKey, subVal]) => (
                <div key={`${key}.${String(subKey)}`}>
                  {renderField(`${key}.${String(subKey)}`, subVal)}
                </div>
              ))}
            </Card>
          </div>
        );
      }

      return null;
    },
    [selectedFieldKeys, toggleKey],
  );

  const tabs: TabsProps['items'] = useMemo(
    () => [
      {
        key: 'fields',
        label: 'Extracted Fields',
        children: (
          <div style={{ height: 250, overflowY: 'auto' }}>
            {documents?.length ? (
              documents.map((doc, i) => (
                <div key={i}>
                  {documents.length > 1 && (
                    <Title level={5}>Document {i + 1}</Title>
                  )}
                  {Object.entries(doc?.fields ?? {}).map(([k, v]) =>
                    renderField(k, v),
                  )}
                </div>
              ))
            ) : (
              <Text type="secondary">No fields found.</Text>
            )}
          </div>
        ),
      },
      {
        key: 'raw',
        label: 'Raw Text',
        children: (
          <div style={{ height: 250, overflowY: 'auto' }}>
            <SyntaxHighlighter language="text">
              {rawText ?? ''}
            </SyntaxHighlighter>
          </div>
        ),
      },
    ],
    [documents, rawText, renderField],
  );

  return (
    <Card
      style={{ padding: 0, marginTop: '1rem' }}
      title={selectedFileName && `Extracted from: ${selectedFileName}`}
      extra={
        closable ? (
          <Button
            type="text"
            size="small"
            aria-label="Close"
            onClick={onClose}
            icon={<CloseOutlined />}
          />
        ) : undefined
      }
    >
      <Tabs defaultActiveKey="fields" items={tabs} />
    </Card>
  );
};

export default IdentityExtractionCard;
